import React, { useState, useMemo } from 'react'
import { useMarketingStore } from '../store/marketingStore'
import { Plus, Trash2, Edit2, TrendingUp, DollarSign, Target, BarChart3, PieChart, Zap } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function MarketingCostTrackerPage() {
  // Store
  const {
    platforms,
    campaigns,
    monthlySpend,
    budgets,
    addPlatform,
    updatePlatform,
    deletePlatform,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    updateBudgets,
    addCostToPlatform,
    getTotalSpent,
    getTotalLeads,
    getAverageROI,
    getMostEffectivePlatform,
    getCampaignStats,
    getMarketingMetrics,
  } = useMarketingStore()

  // Modals
  const [showAddCostModal, setShowAddCostModal] = useState(false)
  const [showCampaignModal, setShowCampaignModal] = useState(false)
  const [showEditPlatformModal, setShowEditPlatformModal] = useState(false)
  const [selectedPlatformId, setSelectedPlatformId] = useState(null)
  const [editingCampaign, setEditingCampaign] = useState(null)

  // Form data
  const [costForm, setCostForm] = useState({ cost: 0, leads: 0 })
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    platform: 'facebook',
    budget: 0,
    spent: 0,
    leads: 0,
    status: 'active',
    startDate: '',
    endDate: '',
  })
  const [platformForm, setPlatformForm] = useState({ name: '', color: '#0284c7', spent: 0, leads: 0 })

  // Computed values
  const metrics = useMemo(() => getMarketingMetrics(), [platforms, campaigns])
  const campaignStats = useMemo(() => getCampaignStats(), [campaigns])
  const topPlatform = useMemo(() => getMostEffectivePlatform(), [platforms])

  // Color palette
  const COLORS = ['#0284c7', '#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981']

  // Add Cost Handlers
  const handleAddCost = (platformId) => {
    setSelectedPlatformId(platformId)
    setCostForm({ cost: 0, leads: 0 })
    setShowAddCostModal(true)
  }

  const handleSaveCost = () => {
    if (costForm.cost <= 0) {
      alert('Please enter a valid cost amount')
      return
    }

    addCostToPlatform(selectedPlatformId, costForm.cost, costForm.leads)
    setShowAddCostModal(false)
    setCostForm({ cost: 0, leads: 0 })
  }

  // Campaign Handlers
  const handleAddCampaign = () => {
    setEditingCampaign(null)
    setCampaignForm({
      name: '',
      platform: 'facebook',
      budget: 0,
      spent: 0,
      leads: 0,
      status: 'active',
      startDate: '',
      endDate: '',
    })
    setShowCampaignModal(true)
  }

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign.id)
    setCampaignForm(campaign)
    setShowCampaignModal(true)
  }

  const handleSaveCampaign = () => {
    if (!campaignForm.name) {
      alert('Please enter campaign name')
      return
    }

    if (editingCampaign) {
      updateCampaign(editingCampaign, campaignForm)
    } else {
      addCampaign(campaignForm)
    }
    setShowCampaignModal(false)
  }

  const handleDeleteCampaign = (id) => {
    if (window.confirm('Delete this campaign?')) {
      deleteCampaign(id)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Marketing Cost Tracker</h1>
        <p className="text-slate-400">Track marketing spend by platform and manage campaigns</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Total Spent */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-400 text-sm font-medium">Total Spent</h3>
            <DollarSign className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">${(metrics.totalSpent / 1000).toFixed(1)}K</div>
          <div className="text-sm text-slate-400">${metrics.remainingBudget} remaining</div>
        </div>

        {/* Total Leads */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-400 text-sm font-medium">Total Leads</h3>
            <Target className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">{metrics.totalLeads}</div>
          <div className="text-sm text-slate-400">Cost per lead: ${metrics.costPerLead}</div>
        </div>

        {/* Average ROI */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-400 text-sm font-medium">Average ROI</h3>
            <TrendingUp className="w-5 h-5 text-green-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">{metrics.averageROI}%</div>
          <div className="text-sm text-green-400">Leads per $100</div>
        </div>

        {/* Budget Utilization */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6 hover:border-purple-400/40 transition-all">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-400 text-sm font-medium">Budget Used</h3>
            <Zap className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">{metrics.budgetUtilization}%</div>
          <div className="text-sm text-slate-400">Monthly: ${(budgets.monthly / 1000).toFixed(0)}K</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Monthly Spend Trend */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            Monthly Spend Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlySpend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }} />
              <Legend />
              <Line type="monotone" dataKey="facebook" stroke="#1877F2" strokeWidth={2} />
              <Line type="monotone" dataKey="google" stroke="#4285F4" strokeWidth={2} />
              <Line type="monotone" dataKey="linkedin" stroke="#0A66C2" strokeWidth={2} />
              <Line type="monotone" dataKey="instagram" stroke="#E4405F" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Platform Comparison */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-cyan-400" />
            Spending by Platform
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={platforms}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #06b6d4' }} />
              <Bar dataKey="spent" fill="#06b6d4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Platforms & Campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Platforms Section */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-white flex items-center gap-2">
              <PieChart className="w-6 h-6 text-blue-400" />
              Platforms ({platforms.length})
            </h3>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {platforms.map((platform) => (
              <div
                key={platform.id}
                className="bg-slate-700/50 rounded-lg p-4 border border-slate-600 hover:border-blue-500/40 transition-all"
                style={{ borderLeftColor: platform.color, borderLeftWidth: '4px' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{platform.icon}</span>
                    <div>
                      <h4 className="text-white font-semibold">{platform.name}</h4>
                      <p className="text-sm text-slate-400">{platform.leads} leads</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAddCost(platform.id)}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center gap-1 transition-all"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>

                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-blue-400">Spent: ${(platform.spent / 1000).toFixed(1)}K</span>
                  <span className="text-green-400">ROI: {platform.roi}%</span>
                </div>

                <div className="w-full bg-slate-600 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{
                      width: `${Math.min((platform.spent / 5000) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          {/* Platform Stats */}
          {platforms.length > 0 && topPlatform.id && (
            <div className="mt-6 pt-6 border-t border-slate-600">
              <p className="text-slate-400 text-sm mb-2">Most Effective: {topPlatform.name}</p>
              <p className="text-2xl font-bold text-green-400">{topPlatform.roi}% ROI</p>
            </div>
          )}
        </div>

        {/* Campaigns Section */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-white flex items-center gap-2">
              <Target className="w-6 h-6 text-purple-400" />
              Campaigns ({campaigns.length})
            </h3>
            <button
              onClick={handleAddCampaign}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>

          {campaigns.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600 hover:border-purple-500/40 transition-all">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-white font-semibold">{campaign.name}</h4>
                      <p className="text-xs text-slate-400">{campaign.startDate} to {campaign.endDate}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditCampaign(campaign)}
                        className="p-2 hover:bg-slate-600 rounded transition-all"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-cyan-400" />
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        className="p-2 hover:bg-slate-600 rounded transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      campaign.status === 'active' ? 'bg-green-500/20 text-green-400' :
                      campaign.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {campaign.status}
                    </span>
                    <span className="text-purple-400">${campaign.spent} / ${campaign.budget}</span>
                    <span className="text-blue-400">{campaign.leads} leads</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">No campaigns yet. Click "Add" to create one.</div>
          )}

          {/* Campaign Stats */}
          {campaigns.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-600 grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-slate-400">Active</p>
                <p className="text-2xl font-bold text-green-400">{campaignStats.active}</p>
              </div>
              <div>
                <p className="text-slate-400">Total Budget</p>
                <p className="text-xl font-bold text-purple-400">${(campaignStats.totalBudget / 1000).toFixed(0)}K</p>
              </div>
              <div>
                <p className="text-slate-400">Total Leads</p>
                <p className="text-xl font-bold text-blue-400">{campaignStats.totalLeads}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ADD COST MODAL */}
      {showAddCostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-blue-500/20 max-w-md w-full mx-4 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Add Marketing Cost</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Cost ($)</label>
                <input
                  type="number"
                  value={costForm.cost}
                  onChange={(e) => setCostForm({ ...costForm, cost: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-2">Leads Generated</label>
                <input
                  type="number"
                  value={costForm.leads}
                  onChange={(e) => setCostForm({ ...costForm, leads: parseInt(e.target.value) || 0 })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAddCostModal(false)}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCost}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all"
              >
                Add Cost
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CAMPAIGN MODAL */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-purple-500/20 max-w-md w-full mx-4 p-6 max-h-96 overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-4">{editingCampaign ? 'Edit Campaign' : 'Add Campaign'}</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Campaign Name</label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  placeholder="e.g., Q2 Brand Awareness"
                />
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-2">Platform</label>
                <select
                  value={campaignForm.platform}
                  onChange={(e) => setCampaignForm({ ...campaignForm, platform: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                >
                  {platforms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Budget ($)</label>
                  <input
                    type="number"
                    value={campaignForm.budget}
                    onChange={(e) => setCampaignForm({ ...campaignForm, budget: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Spent ($)</label>
                  <input
                    type="number"
                    value={campaignForm.spent}
                    onChange={(e) => setCampaignForm({ ...campaignForm, spent: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-2">Leads</label>
                <input
                  type="number"
                  value={campaignForm.leads}
                  onChange={(e) => setCampaignForm({ ...campaignForm, leads: parseInt(e.target.value) || 0 })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  placeholder="0"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Start Date</label>
                  <input
                    type="date"
                    value={campaignForm.startDate}
                    onChange={(e) => setCampaignForm({ ...campaignForm, startDate: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-2">End Date</label>
                  <input
                    type="date"
                    value={campaignForm.endDate}
                    onChange={(e) => setCampaignForm({ ...campaignForm, endDate: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-2">Status</label>
                <select
                  value={campaignForm.status}
                  onChange={(e) => setCampaignForm({ ...campaignForm, status: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCampaignModal(false)}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCampaign}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all"
              >
                {editingCampaign ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
